import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetUserInfoQuery } from '@features/user';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import { isViewOnlyAdmin } from '@utils/roleUtils';

interface RecordingPermissionGateProps {
  children: ReactNode;
}

// Group IDs where admin users are allowed to record
const ADMIN_RECORDING_ALLOWLIST = [
  '6ba543ea-de84-4052-a289-b28282cd5f49', // Neuropsychiatric Institute
];

export const RecordingPermissionGate = ({ children }: RecordingPermissionGateProps) => {
  const navigate = useNavigate();
  const { data: userInfo } = useGetUserInfoQuery(undefined);

  const groupUserRoles = userInfo?.group_user_roles || [];
  const groupIds = userInfo?.group_ids || [];

  // Block users who have view-only admin roles (admin without provider access)
  // UNLESS they belong to an allowlisted group
  const isViewOnly = isViewOnlyAdmin(groupUserRoles);

  // Check if user belongs to any allowlisted group
  const isInAllowlistedGroup = groupIds.some(groupId =>
    ADMIN_RECORDING_ALLOWLIST.includes(groupId),
  );

  // Allow recording if user is in an allowlisted group
  const shouldBlockRecording = isViewOnly && !isInAllowlistedGroup;

  if (shouldBlockRecording) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 3,
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Access Restricted
            </Typography>
            <Typography variant="body2">
              This account does not have recording permissions. Please contact your organization
              administrator if you believe this is a mistake.
            </Typography>
          </Alert>

          <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
            Go to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  // User has permission, render children
  return <>{children}</>;
};
